/**
 * Українські назви модулів і документних таблиць (Порція Виробництво.5,
 * 2026-05-22, UX γ).
 *
 * Призначення — український label замість raw `module-code` / `doc.table`
 * у 4 точках UI:
 *  1. RoleEditorDialog — групування permissions по модулях.
 *  2. BusinessTemplateEditorDialog — чекбокси enabled_modules.
 *  3. ConfirmModuleNarrowingModal — попередження «X документів буде
 *     приховано у модулі Y» з людськими назвами.
 *  4. (опц.) sidebar group labels — вже українські у sidebarItems.ts.
 *
 * Узгоджено з:
 *  - `frontend/src/layouts/sidebarItems.ts` (поле `module` у link items)
 *  - `backend/src/app/services/business_template_service.py` MODULE_TO_DOC_TABLES
 */

export const MODULE_NAMES_UK: Record<string, string> = {
  refs: 'Довідники',
  sales: 'Продажі',
  purchases: 'Закупки',
  cash: 'Каса',
  bank: 'Банк',
  warehouse: 'Склад',
  tax: 'Податки',
  accounting: 'Бухгалтерія',
  reports: 'Звіти',
  assets: 'Основні засоби',
  fixed_assets: 'Основні засоби', // backend alias
  payroll: 'Кадри і ЗП',
  production: 'Виробництво',
  retail: 'Роздріб',
  // settlements — розділ «Розрахунки» у sidebar: акти звірки, довіреності,
  // інвентаризація розрахунків (НЕ договори — ті у картці контрагента).
  settlements: 'Розрахунки з контрагентами',
  // recognition — оголошений як ModuleCode, але у sidebar на нього не
  // прив'язано жодного пункту (модуль-привид). Назву не задаємо.
  returnable_packaging: 'Облік тари',  // Порція C, 2026-06-07
};

/**
 * Отримати українську назву модуля. Якщо код невідомий — повертає сам код
 * (fallback, щоб не загубити інформацію).
 */
export function moduleName(code: string): string {
  return MODULE_NAMES_UK[code] ?? code;
}

/**
 * Українські назви документних doc.* таблиць — для відображення у
 * confirmation modal «N документів у таблиці X буде приховано».
 *
 * Узгоджено з MODULE_TO_DOC_TABLES у backend сервісі.
 */
export const DOC_TABLE_NAMES_UK: Record<string, string> = {
  // production
  'doc.nomenclature_specifications': 'Специфікації',
  'doc.production_reports': 'Звіти виробництва за зміну',
  'doc.product_assemblies': 'Комплектація',
  'doc.product_disassemblies': 'Розукомплектація',
  'doc.material_transfers_for_processing': 'Передача в переробку',
  'doc.processing_receipts': 'Поступлення з переробки',
  'doc.material_receipts_for_processing': 'Прийняття чужої сировини',
  'doc.processing_services_sales': 'Послуги переробки (продаж)',
  'doc.production_service_acts': 'Акти виробничих послуг',
  'doc.wip_inventory': 'Інвентаризація НЗВ',
  // sales
  'doc.goods_sales': 'Реалізації товарів',
  'doc.customer_orders': 'Замовлення покупців',
  'doc.goods_returns_from_customer': 'Повернення від покупця',
  // purchases
  'doc.goods_receipts': 'Прибуткові накладні',
  'doc.supplier_orders': 'Замовлення постачальникам',
  'doc.customs_declarations': 'ВМД (імпорт)',
  'doc.additional_expenses': 'Додаткові витрати',
  'doc.goods_returns_to_supplier': 'Повернення постачальнику',
  // warehouse
  'doc.goods_transfers': 'Переміщення товарів',
  'doc.inventory_counts': 'Інвентаризація',
  'doc.goods_recognitions': 'Оприбуткування лишків',
  'doc.goods_writeoffs': 'Списання товарів',
  // bank/cash
  'doc.bank_receipts': 'Банк (надходження)',
  'doc.bank_payments': 'Банк (списання)',
  'doc.cash_receipts': 'Каса (надходження)',
  'doc.cash_payments': 'Каса (видача)',
  'doc.advance_reports': 'Авансові звіти',
  // tax
  'doc.tax_invoices_outgoing': 'ПН (вихідні)',
  'doc.tax_invoices_incoming': 'ПН (вхідні)',
  // accounting
  'doc.manual_operations': 'Ручні операції',
  'doc.debt_adjustments': 'Взаємозалік',
  'doc.doubtful_debt_reserves': 'Резерв сумнівних',
  'doc.fx_revaluations': 'Переоцінка валюти',
  // payroll
  'doc.hire_orders': 'Прийом на роботу',
  'doc.staff_transfers': 'Кадрові переміщення',
  'doc.dismissal_orders': 'Звільнення',
  'doc.timesheets': 'Табель робочого часу',
  'doc.payroll_documents': 'Документи ЗП',
  // fixed_assets
  'doc.fixed_asset_acceptances': 'Прийняття ОЗ',
  'doc.fixed_asset_disposals': 'Вибуття ОЗ',
  'doc.fixed_asset_modernizations': 'Модернізація ОЗ',
  // retail (Порція Роздріб.5a, 2026-05-23)
  'doc.retail_sales': 'Чеки роздрібного продажу',
  'doc.retail_returns': 'Повернення (роздріб)',
  'doc.cash_shift_open': 'Відкриття зміни',
  'doc.cash_shift_close': 'Закриття зміни (Z-звіт)',
  'doc.customer_card_issue': 'Видача карток',
  'doc.loyalty_transactions': 'Бонусні транзакції',
  'doc.price_change': 'Зміна цін',
  'doc.online_store_order': 'Замовлення з інтернет-магазину',
};

export function docTableName(table: string): string {
  return DOC_TABLE_NAMES_UK[table] ?? table;
}
